import React, { useEffect, useMemo, useCallback } from 'react'
import PlanArea from './area'
import Seat from './seat'
import useSWR from 'swr'
import Fetcher from '@lib/fetcher'
import { ChevronRightIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import spektrixLogo from '@images/SpektrixLogo_700px.png'
import BookingFeeDisclosure from '../../booking-fee-disclosure'

const InstanceSeating = ({ instance, model, onClose }) => {
  const [seatsReady, setSeatsReady] = React.useState(false)
  const scrollContainerRef = React.useRef(null)
  const planContainerRef = React.useRef(null)

  // Fetch all seats for the instance (including area seats)
  const { data: instanceSeats } = useSWR(
    `/instances/${instance.spektrix_v3_id || instance.id}/seats`,
    Fetcher
  )

  // Fetch availability status for all seats including child plans/areas
  const { data: availableSeats } = useSWR(
    `/instances/${
      instance.spektrix_v3_id || instance.id
    }/status/detail?includeChildPlans=true`,
    Fetcher
  )

  useEffect(() => {
    if (instanceSeats) {
      // Use setTimeout to avoid the setState during render warning
      setTimeout(() => {
        model.instanceSeats = instanceSeats
      }, 0)
    }
  }, [instanceSeats])

  useEffect(() => {
    if (availableSeats) {
      // Use setTimeout to avoid the setState during render warning
      setTimeout(() => {
        model.availableSeats = availableSeats
      }, 0)
    }
  }, [availableSeats])

  // Check if seats are ready by looking at the model's seats
  useEffect(() => {
    if (
      instanceSeats &&
      availableSeats &&
      model.seats &&
      model.seats.length > 0
    ) {
      // All data is loaded and processed
      const allSeatsHaveStatus = model.seats.every(
        seat => seat.loading === false
      )
      if (allSeatsHaveStatus) {
        // Add a small delay to ensure the model has fully updated
        setTimeout(() => {
          setSeatsReady(true)
        }, 100)
      }
    }
  }, [instanceSeats, availableSeats, model.seats])

  // Scroll to center the plan when it loads
  useEffect(() => {
    if (
      model?.plan?.backgroundImageUrl &&
      scrollContainerRef.current &&
      planContainerRef.current
    ) {
      // Wait for image to load
      const img = planContainerRef.current.querySelector('img')
      if (img) {
        const scrollToCenter = () => {
          const container = scrollContainerRef.current
          const plan = planContainerRef.current

          // Calculate center position
          const containerWidth = container.offsetWidth
          const planWidth = plan.offsetWidth

          if (planWidth > containerWidth) {
            // Scroll to center horizontally
            container.scrollLeft = (planWidth - containerWidth) / 2
          }
        }

        if (img.complete) {
          scrollToCenter()
        } else {
          img.addEventListener('load', scrollToCenter)
          return () => img.removeEventListener('load', scrollToCenter)
        }
      }
    }
  }, [model?.plan?.backgroundImageUrl])

  // Build an id -> seat map once per seats change so child lookups are O(1)
  // (replaces the previous O(seats^2) find-in-map in PlanArea).
  const seatsById = useMemo(() => {
    const map = new Map()
    for (const seat of model.seats || []) map.set(seat.id, seat)
    return map
  }, [model.seats])

  // Stable click handler so React.memo(Seat) holds: the add/remove toggle branch
  // lives in the model (it knows the current basket), so this callback keeps a
  // constant identity across renders and only the toggled seat re-renders.
  const onSelect = useCallback(seatId => model.toggleSeat(seatId), [model])

  return (
    <div className='flex flex-col gap-3 w-full h-full sm:h-auto'>
      <BookingFeeDisclosure className='text-xs text-gray-600 dark:text-gray-400' />
      <div className='flex flex-col sm:flex-row sm:items-center gap-2 w-full justify-between'>
        <div className='text-gray-700 dark:text-gray-300 text-left'>
          {!seatsReady ? (
            <span className='text-sm'>Loading seat availability...</span>
          ) : model.seatsInBasket.length > 0 ? (
            `Selected seats: ${new Intl.ListFormat('en', {
              style: 'long',
              type: 'conjunction'
            }).format(model.seatsInBasket.map(seat => seat.name))}`
          ) : (
            `Select seats to add or modify your basket`
          )}
        </div>

        <button
          className={clsx(
            'disabled:opacity-50',
            'rounded',
            'bg-blue-600 text-white dark:bg-blue-500',
            'px-2 py-1',
            'text-sm flex items-center space-x-1 whitespace-nowrap',
            'hover:bg-blue-700 dark:hover:bg-blue-600',
            'justify-center'
          )}
          disabled={model.seatsInBasket.length === 0 && !model.onlyDeleting}
          onClick={() => {
            if (model.onlyDeleting) {
              model.addToBasket()
            } else {
              model.showTicketTypes = true
            }
          }}
        >
          <span>
            {!model.onlyDeleting ? 'Confirm ticket Types' : 'Update Basket'}
          </span>
          <ChevronRightIcon className='w-4 h-4' />
        </button>
      </div>
      <div className='relative overflow-hidden rounded-lg'>
        <div
          ref={scrollContainerRef}
          className='overflow-x-auto overflow-y-hidden -mx-3 px-3 md:mx-0 md:px-0 md:flex md:justify-center rounded-lg'
        >
          <div
            ref={planContainerRef}
            className='inline-block relative rounded-lg overflow-hidden'
          >
            <img
              src={model?.plan.backgroundImageUrl}
              className='block max-w-none'
              alt='Seating plan'
            />

            {model.plan?.areas?.map((area, key) => (
              <PlanArea
                instanceId={instance.spektrix_v3_id || instance.id}
                area={area}
                modelSeats={model.seats}
                seatsById={seatsById}
                onSelect={onSelect}
                key={key}
              />
            ))}
            {/* Render seats that don't belong to any area */}
            {model.seats &&
              model.seats
                .filter(seat => {
                  // Only render seats here if they're not part of any area
                  const belongsToArea = model.plan?.areas?.some(area => {
                    // Check if seat has explicit area assignment
                    if (seat.areaId === area.id || seat.area === area.id)
                      return true

                    // Only check bounds if area doesn't have its own seats
                    const areaHasOwnSeats =
                      area.seats && Object.keys(area.seats).length > 0
                    if (
                      !areaHasOwnSeats &&
                      area.x &&
                      area.y &&
                      area.width &&
                      area.height &&
                      seat.x &&
                      seat.y
                    ) {
                      return (
                        seat.x >= area.x &&
                        seat.x <= area.x + area.width &&
                        seat.y >= area.y &&
                        seat.y <= area.y + area.height
                      )
                    }
                    return false
                  })
                  return !belongsToArea
                })
                .map(seat => (
                  <Seat
                    key={seat.id}
                    seat={seat}
                    seatId={seat.id}
                    onSelect={onSelect}
                  />
                ))}
          </div>
        </div>

        {/* Loading overlay - positioned over the visible area */}
        {!seatsReady && (
          <div className='absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-center z-20 pointer-events-none'>
            <div className='bg-white dark:bg-gray-800 rounded-lg p-4 shadow-xl flex flex-col items-center gap-2 border border-gray-200 dark:border-gray-700 mx-4 pointer-events-auto'>
              <div className='w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin'></div>
              <div className='text-center'>
                <p className='text-sm font-semibold text-gray-800 dark:text-gray-200'>
                  Checking seat availability
                </p>
                <p className='text-xs text-gray-600 dark:text-gray-400 mt-1'>
                  This may take a few seconds...
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Spektrix Logo */}
      <div className='mt-4 pt-2 border-t border-gray-200 dark:border-gray-700 flex justify-center'>
        <div className='flex items-center gap-1'>
          <p className='text-[10px] text-gray-400 dark:text-gray-500'>
            Powered by
          </p>
          <div className='bg-gray-100 dark:bg-gray-200 rounded px-1.5 py-0.5 inline-block'>
            <img src={spektrixLogo} alt='Spektrix' className='h-3' />
          </div>
        </div>
      </div>
    </div>
  )
}

export default InstanceSeating
